"use client"

import React from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight, CircleCheck, CircleEllipsis } from "lucide-react"

export type SectionStatus = 'not-started' | 'in-progress' | 'completed'

const statusConfig = {
  'completed': {
    icon: CircleCheck,
    bgColor: 'bg-success/10',
    borderColor: 'border-success/20',
    textColor: 'text-success',
    label: 'Completed'
  },
  'in-progress': {
    icon: CircleEllipsis,
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary/20',
    textColor: 'text-primary',
    label: 'In progress'
  },
  'not-started': {
    icon: null,
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-300',
    textColor: 'text-gray-600',
    label: 'Not started'
  }
} as const

export function SectionStatusBadge({ status }: { status: SectionStatus }) {
  const config = statusConfig[status]
  const Icon = config.icon
  
  return (
    <div className={`inline-flex items-center gap-1.5 h-8 px-3 ${config.bgColor} border ${config.borderColor} rounded-full`}>
      {Icon && <Icon className={`h-3.5 w-3.5 ${config.textColor}`} strokeWidth={2.5} />}
      <span className={`text-xs font-medium ${config.textColor}`}>
        {config.label}
      </span>
    </div>
  )
}

export interface FormSectionCardProps {
  id: string
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  isCompleted: boolean
  showPrevNext?: boolean
  onPrevious?: () => void
  onNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
  nextButtonOverride?: React.ReactNode
  children: React.ReactNode
}

export const FormSectionCard = ({
  id,
  title,
  description,
  icon: IconComponent,
  isCompleted,
  showPrevNext = false,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  nextButtonOverride,
  children
}: FormSectionCardProps) => {
  return (
    <section id={id} className="scroll-mt-24">
      <Card className="overflow-hidden border">
        <CardHeader className="flex flex-row items-center justify-between bg-primary/5 px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <IconComponent className="h-7 w-7 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold">
                {title}
              </CardTitle>
              <CardDescription className="text-sm">
                {description}
              </CardDescription>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Status badge in header */}
            <SectionStatusBadge status={isCompleted ? 'completed' : 'in-progress'} />
            
            {/* Navigation buttons in header */}
            {showPrevNext && (
              <div className="flex items-center gap-1">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onPrevious}
                  disabled={!hasPrevious}
                  className="h-8 w-8 p-0 rounded-full"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onNext}
                  disabled={!hasNext}
                  className="h-8 w-8 p-0 rounded-full border-primary text-primary hover:text-primary"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6 p-6">
          {children}
        </CardContent>
        
        {/* Navigation Footer */}
        {showPrevNext && (
          <CardFooter className="bg-gray-50/50 border-t px-6 py-4">
            <div className="flex justify-end w-full">
              <div className="flex items-center gap-1">
                <Button 
                  variant="ghost" 
                  size="default"
                  onClick={onPrevious}
                  disabled={!hasPrevious}
                  className="flex items-center gap-3 px-6 py-1.5 text-base font-medium"
                >
                  <ChevronLeft className="h-6 w-6" />
                  Previous
                </Button>
                {nextButtonOverride || (
                  <Button 
                    variant="ghost" 
                    size="default"
                    onClick={onNext}
                    disabled={!hasNext}
                    className="flex items-center gap-3 px-6 py-1.5 text-base font-medium text-primary hover:text-primary/80"
                  >
                    Next
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </div>
          </CardFooter>
        )}
      </Card>
    </section>
  )
}
